/** @jsxImportSource @emotion/react */
import { DatePicker, DatePickerProps, DatesRangeValue } from '@mantine/dates';
import { useCallback } from 'react';
import { useTheme } from '../../ThemeProvider';
import { UtilityButton } from '../UtilityButton';
import { TimeInput } from './components';
import '@mantine/core/styles.layer.css';
import '@mantine/dates/styles.layer.css';

type DefaultProps = DatePickerProps<'default'>;
type MultipleProps = DatePickerProps<'multiple'>;

type RangeProps = DatePickerProps<'range'>;

export type Props = (DefaultProps | RangeProps | MultipleProps) & {
  withTime?: boolean;
};

export const Datepicker = ({
  value,
  type = 'default',
  withTime,
  onChange,
  ...rest
}: Props) => {
  const { spacing, palette, shape } = useTheme();

  const handleReset = useCallback(() => {
    if (!onChange) return;
    if (type === 'default') onChange(null as any);
    if (type === 'range') onChange([null, null] as any);
    if (type === 'multiple') onChange([] as any);
  }, [type, onChange]);

  return (
    <div
      css={{
        position: 'relative',
        backgroundColor: palette.white,
        display: 'flex',
        flexDirection: 'column',
        gap: spacing.xs,
        borderRadius: shape.borderRadius,
        padding: spacing.xxs,
      }}
    >
      <DatePicker
        {...rest}
        type={type}
        value={value}
        allowSingleDateInRange
        onChange={(e) => {
          if (onChange !== undefined) {
            if (
              type === 'range' &&
              Array.isArray(e) &&
              e[1] !== null &&
              e[1].getHours() === 0 &&
              e[1].getMinutes() === 0
            ) {
              e[1].setHours(23, 59, 59, 999);
            }
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            onChange(e);
          }
        }}
        monthLabelFormat="MMM YYYY"
        withCellSpacing={false}
        css={{
          '.mantine-DatePicker-calendarHeader': { maxWidth: 'fit-content' },
          '.mantine-DatePicker-day[data-selected]': {
            fontWeight: '500',
            color: `${palette.white} !important`,
            borderRadius: '50% !important',
            position: 'relative',
          },
          '.mantine-DatePicker-monthCell': {
            position: 'relative',
            zIndex: 1,
          },
          '.mantine-DatePicker-day[data-first-in-range]:before': {
            content: '""',
            backgroundColor: palette.primary.x50,
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderTopLeftRadius: '50%',
            borderBottomLeftRadius: '50%',
            zIndex: -1,
          },
          '.mantine-DatePicker-day[data-last-in-range]:before': {
            content: '""',
            backgroundColor: palette.primary.x50,
            position: 'absolute',
            width: '100%',
            height: '100%',
            borderTopRightRadius: '50%',
            borderBottomRightRadius: '50%',
            zIndex: -1,
          },
          '.mantine-DatePicker-day[data-in-range]': {
            fontWeight: '500',
            color: palette.primary.default,
          },
          '.mantine-DatePicker-monthRow :last-of-type > .mantine-DatePicker-day[data-in-range]':
            {
              borderTopRightRadius: shape.borderRadius,
              borderBottomRightRadius: shape.borderRadius,
            },
          '.mantine-DatePicker-monthRow :first-of-type > .mantine-DatePicker-day[data-in-range]':
            {
              borderTopLeftRadius: shape.borderRadius,
              borderBottomLeftRadius: shape.borderRadius,
            },
          '.mantine-DatePicker-day[data-first-in-range]': {
            borderTopLeftRadius: shape.borderRadius,
            borderBottomLeftRadius: shape.borderRadius,
          },
          '.mantine-DatePicker-day[data-last-in-range]': {
            borderTopRightRadius: shape.borderRadius,
            borderBottomRightRadius: shape.borderRadius,
          },
        }}
        weekendDays={[]}
      />
      <div css={{ position: 'absolute', top: spacing.xxs, right: spacing.xxs }}>
        <UtilityButton text="Reset" variant="text" onClick={handleReset} />
      </div>
      {withTime && (
        <>
          {type === 'default' && (
            <TimeInput
              value={value as Date | null}
              onChange={(e) => {
                onChange && onChange(e as any);
              }}
            />
          )}
          {type === 'range' && (
            <div
              css={{
                display: 'grid',
                gap: spacing.xxs,
                gridTemplateColumns: '1fr 1fr',
              }}
            >
              <TimeInput
                value={(value as DatesRangeValue)[0]}
                onChange={(e) => {
                  const values = value as DatesRangeValue;
                  onChange && onChange([e, values[1]] as any);
                }}
              />
              <TimeInput
                value={(value as DatesRangeValue)[1]}
                onChange={(e) => {
                  const values = value as DatesRangeValue;
                  onChange && onChange([values[0], e] as any);
                }}
              />
            </div>
          )}
          {type === 'multiple' && (
            <div>
              {(value as Date[]).map((d, idx) => (
                <TimeInput
                  key={idx}
                  value={d}
                  onChange={(e) => {
                    const values = value as Date[];
                    const first = values.slice(0, idx);
                    const last = values.slice(idx + 1);
                    onChange && onChange([...first, e, ...last] as any);
                  }}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};
